import { useCallback, useEffect, useState } from 'react';
import { photoUrl } from '../lib/photos';

export interface LightboxPhoto {
  /** Slot file name, resolved through photoUrl when `url` is not given. */
  name: string;
  alt: string;
  /** Full-size image for the viewer; defaults to the slot photo. */
  url?: string;
  /** Smaller image for the grid tile; defaults to `url`. */
  thumb?: string;
}

function fullUrl(photo: LightboxPhoto): string | undefined {
  return photo.url ?? photoUrl(photo.name);
}

interface GalleryProps {
  photos: LightboxPhoto[];
  className?: string;
}

/**
 * Photo grid that opens a full-screen viewer: keyboard arrows and Escape,
 * swipe-free tap targets on the sides, focus returned to the tile on close.
 * Missing files fall back to the same "photo goes here" slot as <Photo>.
 */
export function Gallery({ photos, className }: GalleryProps) {
  const [open, setOpen] = useState<number | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (delta: number) =>
      setOpen((i) => (i === null ? i : (i + delta + photos.length) % photos.length)),
    [photos.length],
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, close, step]);

  const current = open === null ? null : photos[open];
  const currentUrl = current ? fullUrl(current) : null;

  return (
    <>
      <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${className ?? ''}`}>
        {photos.map((photo, i) => {
          const url = fullUrl(photo);
          const tile = photo.thumb ?? url;
          return (
            <button
              key={photo.name}
              type="button"
              onClick={() => url && setOpen(i)}
              disabled={!url}
              aria-label={url ? `Open photo: ${photo.alt}` : `${photo.alt} (photo coming soon)`}
              className="group relative aspect-[4/3] overflow-hidden border border-white/10 bg-night-900 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-500 disabled:cursor-default"
            >
              {url ? (
                <img
                  src={tile}
                  alt={photo.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center bg-hex text-center text-sm uppercase tracking-wide text-ink-400">
                  Photo goes here
                  <br />
                  {photo.name}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {current && currentUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={current.alt}
          className="fixed inset-0 z-50 flex items-center justify-center bg-night-950/95 p-4"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-4 top-4 px-3 py-2 text-2xl text-ink-200 hover:text-white"
          >
            ×
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 -translate-y-1/2 px-3 py-6 text-3xl text-ink-200 hover:text-white"
          >
            ‹
          </button>
          <img
            src={currentUrl}
            alt={current.alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-full object-contain"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            aria-label="Next photo"
            className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-6 text-3xl text-ink-200 hover:text-white"
          >
            ›
          </button>
          <p className="absolute bottom-4 left-0 right-0 text-center text-sm text-ink-400">
            {open! + 1} of {photos.length}
            {current.alt ? ` · ${current.alt}` : ''}
          </p>
        </div>
      )}
    </>
  );
}
